<template>
  <!-- DUPLICATED from packages/frontend for the studio (intentional — see
       docs/superpowers/specs/2026-06-01-battleplan-authoring-tool-design.md §5).
       Keep rendering in sync with the frontend original when it changes. -->
  <div class="scoring-controls">
    <!-- Resource counters (persistent, not per-round) -->
    <div v-for="rule in resourceRules" :key="'res-' + rule.id" class="scoring-row resource-row">
      <div class="resource-label">{{ rule.resource }}</div>
      <CounterBox
        :model-value="resources?.[rule.resource!] ?? 0"
        :min="0"
        :max="rule.maxCount ?? 99"
        @update:model-value="updateResource(rule.resource!, $event)"
      />
    </div>

    <!-- Scoring rules for current round -->
    <template v-for="rule in activeRules" :key="rule.id">
      <!-- Checkbox rule -->
      <div
        v-if="rule.controlType === 'checkbox'"
        class="scoring-row checkbox-row"
        @click="toggleCheckbox(rule.id)"
      >
        <span class="checkbox-icon">
          <FontAwesomeIcon
            :icon="scoring[rule.id] ? 'check-circle' : 'circle'"
            :class="{ checked: scoring[rule.id] }"
          />
        </span>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span
          class="condition-text"
          :class="{ completed: scoring[rule.id] }"
          v-html="formatText(rule.condition)"
        />
        <span class="points-badge" :class="{ earned: scoring[rule.id] }"> +{{ rule.points }} </span>
      </div>

      <!-- Counter rule -->
      <div v-else-if="rule.controlType === 'counter'" class="scoring-row counter-row">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span class="condition-text" v-html="formatText(rule.condition)" />
        <div class="counter-controls">
          <CounterBox
            :model-value="(typeof scoring[rule.id] === 'number' ? scoring[rule.id] : 0) as number"
            :min="0"
            :max="rule.maxCount ?? 10"
            @update:model-value="updateCounter(rule.id, $event)"
          />
          <span
            class="points-badge"
            :class="{ earned: typeof scoring[rule.id] === 'number' && scoring[rule.id] > 0 }"
            >+{{ rule.points }} each</span
          >
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
// DUPLICATED from packages/frontend for the studio (intentional — see
// docs/superpowers/specs/2026-06-01-battleplan-authoring-tool-design.md §5).
// Keep rendering in sync with the frontend original when it changes.
//
// Import rewiring vs original:
//   - CounterBox → ./CounterBox.vue (local copy)
//   - formatText → ../utils/formatter.js (local copy)
import type { IScoringRule } from '@scrolldex/shared';
import CounterBox from './CounterBox.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { formatText } from '../utils/formatter.js';
import { computed } from 'vue';

const props = defineProps<{
  rules: IScoringRule[];
  scoring: Record<string, boolean | number>;
  round: number;
  resources?: Record<string, number>;
}>();

const emit = defineEmits<{
  (e: 'update:scoring', value: Record<string, boolean | number>): void;
  (e: 'update:resources', value: Record<string, number>): void;
}>();

/** Rules with a resource field — rendered as persistent counters outside the round filter */
const resourceRules = computed(() => props.rules.filter((r) => r.resource));

/** Rules active for the current round (excluding resource-only rules) */
const activeRules = computed(() => {
  const result = props.rules.filter((r) => {
    if (r.resource) return false;
    if (r.rounds && !r.rounds.includes(props.round)) return false;
    return true;
  });
  return result;
});

function toggleCheckbox(ruleId: string) {
  const updated = { ...props.scoring };
  updated[ruleId] = !updated[ruleId];
  emit('update:scoring', updated);
}

function updateCounter(ruleId: string, value: number) {
  const updated = { ...props.scoring };
  updated[ruleId] = value;
  emit('update:scoring', updated);
}

function updateResource(resourceName: string, value: number) {
  const updated = { ...(props.resources ?? {}) };
  updated[resourceName] = value;
  emit('update:resources', updated);
}
</script>

<style scoped>
.scoring-controls {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.scoring-row {
  display: flex;
  align-items: center;
  min-height: 44px;
  /* padding: 6px 8px; */
  padding: 4px;
  border-radius: 6px;
  background: var(--bg-surface);
  border: var(--surface-border);
  /* gap: 8px; */
}

/* Checkbox rows */
.checkbox-row {
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}

.checkbox-row:active {
  background: var(--bg-primary);
}

.checkbox-icon {
  font-size: 1.3em;
  color: var(--text-accent);
  flex-shrink: 0;
  width: 1.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s;
}

.checkbox-icon .checked {
  color: var(--color-green);
}

.condition-text {
  flex: 1;
  font-size: 0.8em;
  color: var(--text-main);
  /* line-height: 1.3; */
}

.condition-text.completed {
  color: var(--text-accent);
}

.points-badge {
  font-weight: 700;
  font-size: 0.88em;
  padding: 2px 8px;
  border-radius: 10px;
  background: var(--bg-primary);
  color: var(--text-accent);
  flex-shrink: 0;
  transition:
    background 0.15s,
    color 0.15s;
}

.points-badge.earned {
  background: var(--color-green);
  color: #fff;
}

/* Counter rows */
.counter-row {
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.counter-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
}

/* Resource rows */
.resource-row {
  background: var(--bg-primary);
  border-style: dashed;
}

.resource-label {
  font-weight: 600;
  font-size: 0.9em;
  color: var(--text-main);
  text-transform: capitalize;
  flex: 1;
}
</style>
